<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CapiSettingsAPI from 'dashboard/wasapi/capi/api';
import {
  FEP_TOTAL_MS,
  RESPONSE_TOTAL_MS,
  formatCountdown,
  progressRatio,
  remainingMs,
} from 'dashboard/wasapi/capi/fepHelpers';
import { CSW_TOTAL_MS, cswDeadlineMs } from './helpers';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
});

const { t } = useI18n();

const now = ref(Date.now());
const fep = ref(null);
let ticker = null;
let refresher = null;

// ---- 24h customer service window (resets with every customer message: the deadline is
// computed from the store messages, so a websocket-delivered incoming message resets it live)
const cswDeadline = computed(() => cswDeadlineMs(props.chat?.messages));
const cswMs = computed(() =>
  cswDeadline.value ? Math.max(0, cswDeadline.value - now.value) : 0
);
const cswOpen = computed(() => cswMs.value > 0);

// ---- 72h free entry point (ads) — server-computed, ticked locally
const fetchFep = async () => {
  if (!props.chat?.id) return;
  try {
    const { data } = await CapiSettingsAPI.conversationStatus(props.chat.id);
    fep.value = data.fep || null;
  } catch {
    fep.value = null;
  }
};

const fepDeadlineMs = computed(() =>
  remainingMs(fep.value?.response_deadline_at, now.value)
);
const fepWindowMs = computed(() =>
  remainingMs(fep.value?.expires_at, now.value)
);
const FEP_STYLES = {
  awaiting_reply: {
    icon: 'i-lucide-alarm-clock',
    box: 'text-n-amber-11',
    bar: 'bg-n-amber-9',
  },
  open: {
    icon: 'i-lucide-badge-dollar-sign',
    box: 'text-n-teal-11',
    bar: 'bg-n-teal-9',
  },
  expired: {
    icon: 'i-lucide-badge-dollar-sign',
    box: 'text-n-ruby-11',
    bar: '',
  },
  missed: { icon: 'i-lucide-clock-alert', box: 'text-n-slate-11', bar: '' },
};
// null when there is no FEP at all (backend status 'none' / no ad entry point)
const fepStatus = computed(() => {
  const status = fep.value?.status;
  if (status === 'awaiting_reply' && fepDeadlineMs.value === 0) return 'missed';
  if (status === 'open' && fepWindowMs.value === 0) return 'expired';
  return FEP_STYLES[status] ? status : null;
});

const fepLabel = computed(() => {
  switch (fepStatus.value) {
    case 'awaiting_reply':
      return t('WASAPI_FEP.AWAITING', {
        time: formatCountdown(fepDeadlineMs.value),
      });
    case 'open':
      return t('WASAPI_FEP.OPEN', { time: formatCountdown(fepWindowMs.value) });
    case 'expired':
      return t('WASAPI_FEP.PAID');
    case 'missed':
      return t('WASAPI_FEP.MISSED');
    default:
      return '';
  }
});
const fepRatio = computed(() =>
  fepStatus.value === 'awaiting_reply'
    ? progressRatio(fepDeadlineMs.value, RESPONSE_TOTAL_MS)
    : progressRatio(fepWindowMs.value, FEP_TOTAL_MS)
);
const fepStyle = computed(() => FEP_STYLES[fepStatus.value]);

onMounted(() => {
  fetchFep();
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  refresher = setInterval(fetchFep, 60_000);
});
onBeforeUnmount(() => {
  clearInterval(ticker);
  clearInterval(refresher);
});
watch(() => props.chat?.id, fetchFep);
// a new outgoing reply can open the FEP window; a new ad click restarts it
watch(
  () => props.chat?.messages?.length,
  () => fetchFep()
);

const visible = computed(() => !!cswDeadline.value || !!fepStatus.value);
</script>

<template>
  <div
    v-if="visible"
    class="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-1.5 text-xs border-t border-n-weak bg-n-alpha-1"
    data-testid="wasapi-window-timers"
  >
    <span
      v-if="cswDeadline"
      v-tooltip.top="{
        content: t('WASAPI_WINDOWS.CSW_TOOLTIP'),
        popperClass: 'max-w-72',
      }"
      class="flex items-center gap-1.5 font-medium tabular-nums"
      :class="cswOpen ? 'text-n-blue-11' : 'text-n-ruby-11'"
      data-testid="csw-timer"
    >
      <Icon
        :icon="
          cswOpen ? 'i-lucide-message-circle' : 'i-lucide-message-circle-off'
        "
        class="size-3.5 shrink-0"
      />
      <template v-if="cswOpen">
        {{ t('WASAPI_WINDOWS.CSW_OPEN', { time: formatCountdown(cswMs) }) }}
      </template>
      <template v-else>{{ t('WASAPI_WINDOWS.CSW_CLOSED') }}</template>
      <span
        v-if="cswOpen"
        class="hidden sm:block w-16 h-1 rounded-full bg-n-alpha-2 overflow-hidden"
      >
        <span
          class="block h-full rounded-full bg-n-blue-9 transition-[width] duration-1000 ease-linear"
          :style="{ width: `${progressRatio(cswMs, CSW_TOTAL_MS) * 100}%` }"
        />
      </span>
    </span>

    <span
      v-if="fepStatus"
      v-tooltip.top="{
        content: t('WASAPI_FEP.TOOLTIP'),
        popperClass: 'max-w-72',
      }"
      class="flex items-center gap-1.5 font-medium tabular-nums"
      :class="fepStyle.box"
      data-testid="fep-timer"
    >
      <Icon :icon="fepStyle.icon" class="size-3.5 shrink-0" />
      {{ fepLabel }}
      <span
        v-if="['awaiting_reply', 'open'].includes(fepStatus)"
        class="hidden sm:block w-16 h-1 rounded-full bg-n-alpha-2 overflow-hidden"
      >
        <span
          class="block h-full rounded-full transition-[width] duration-1000 ease-linear"
          :class="fepStyle.bar"
          :style="{ width: `${fepRatio * 100}%` }"
        />
      </span>
    </span>
  </div>
</template>
